/**
 * SubmissionsPage - View and manage form submissions
 * 
 * HIPAA COMPLIANCE:
 * - List view shows metadata only (no PHI)
 * - PHI decryption requires MFA verification
 * - All PHI access is logged to audit trail
 * - Decrypted data is kept in memory only (not persisted)
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type {
  SubmissionListItem,
  SubmissionStats,
  SubmissionStatus,
  Form,
} from '@formflow/shared';
import { submissionsApi, formsApi, ApiClientError } from '../../lib/api';
import { SubmissionDetailModal } from '../../components/submissions';
import './SubmissionsPage.css';

// Status badge colors
const STATUS_COLORS: Record<SubmissionStatus, { bg: string; text: string }> = {
  complete: { bg: '#dcfce7', text: '#166534' },
  partial: { bg: '#fef3c7', text: '#92400e' },
  abandoned: { bg: '#fee2e2', text: '#991b1b' },
  failed: { bg: '#fecaca', text: '#dc2626' },
};

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

export default function SubmissionsPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();

  // State
  const [formName, setFormName] = useState<string>('');
  const [submissions, setSubmissions] = useState<SubmissionListItem[]>([]);
  const [stats, setStats] = useState<SubmissionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 25;

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal state
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionListItem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Load form info
  useEffect(() => {
    if (!formId) return;

    formsApi.get(formId)
      .then((form: Form) => setFormName(form.name))
      .catch(() => setFormName('Unknown Form'));
  }, [formId]);

  // Load submissions
  const loadSubmissions = useCallback(async () => {
    if (!formId) return;

    setLoading(true);
    setError(null);

    try {
      const [submissionsResult, statsResult] = await Promise.all([
        submissionsApi.listForForm(formId, {
          page,
          page_size: pageSize,
          status: statusFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }),
        submissionsApi.getStats(formId),
      ]);

      setSubmissions(submissionsResult.results);
      setTotalPages(submissionsResult.total_pages);
      setTotalCount(submissionsResult.count);
      setStats(statsResult);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to load submissions');
      }
    } finally {
      setLoading(false);
    }
  }, [formId, page, pageSize, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  // Handle selection
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === submissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(submissions.map(s => s.id)));
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action: 'archive' | 'delete') => {
    if (selectedIds.size === 0) return;

    const confirmMessage = action === 'delete'
      ? `Are you sure you want to delete ${selectedIds.size} submission(s)? This action cannot be undone.`
      : `Are you sure you want to archive ${selectedIds.size} submission(s)?`;

    if (!confirm(confirmMessage)) return;

    try {
      await submissionsApi.bulkAction({
        action,
        submission_ids: Array.from(selectedIds),
        confirm: action === 'delete',
      });

      setSelectedIds(new Set());
      loadSubmissions();
    } catch (err) {
      if (err instanceof ApiClientError) {
        alert(err.message);
      } else {
        alert('Failed to perform action');
      }
    }
  };

  // Handle export
  const handleExport = async (includePhi: boolean, mfaCode?: string) => {
    if (!formId) return;

    setExporting(true);

    try {
      const blob = await submissionsApi.export(formId, {
        format: 'csv',
        include_phi: includePhi,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        status_filter: statusFilter ? [statusFilter as SubmissionStatus] : undefined,
        mfa_code: mfaCode,
      });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formName.replace(/\s+/g, '_')}_submissions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.isForbidden) {
          alert('Invalid MFA code. Please try again.');
        } else {
          alert(err.message);
        }
      } else {
        alert('Failed to export submissions');
      }
    } finally {
      setExporting(false);
    }
  };

  // Open submission detail
  const openSubmissionDetail = (submission: SubmissionListItem) => {
    setSelectedSubmission(submission);
    setShowDetailModal(true);
  };

  if (!formId) {
    return <div className="submissions-error">Form ID is required</div>;
  }

  return (
    <div className="submissions-page">
      {/* Header */}
      <header className="submissions-header">
        <div className="submissions-header-left">
          <Link to="/dashboard/forms" className="back-link">
            ← Back to Forms
          </Link>
          <h1>{formName || 'Loading...'}</h1>
          <span className="submissions-count">{totalCount} submissions</span>
        </div>
        <div className="submissions-header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => handleExport(false)}
            disabled={exporting || totalCount === 0}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/dashboard/forms/${formId}`)}
          >
            Edit Form
          </button>
        </div>
      </header>

      {/* Stats Cards */}
      {stats && (
        <div className="submissions-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-card stat-complete">
            <span className="stat-value">{stats.by_status.complete}</span>
            <span className="stat-label">Complete</span>
          </div>
          <div className="stat-card stat-partial">
            <span className="stat-value">{stats.by_status.partial}</span>
            <span className="stat-label">Partial</span>
          </div>
          <div className="stat-card stat-abandoned">
            <span className="stat-value">{stats.by_status.abandoned}</span>
            <span className="stat-label">Abandoned</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.completion_rate}%</span>
            <span className="stat-label">Completion Rate</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="submissions-filters">
        <div className="filter-group">
          <label>Status</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Statuses</option>
            <option value="complete">Complete</option>
            <option value="partial">Partial</option>
            <option value="abandoned">Abandoned</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="filter-group">
          <label>From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="filter-group">
          <label>To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
        {(statusFilter || dateFrom || dateTo) && (
          <button
            className="btn btn-text"
            onClick={() => {
              setStatusFilter('');
              setDateFrom('');
              setDateTo('');
              setPage(1);
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedIds.size} selected</span>
          <button
            className="btn btn-secondary"
            onClick={() => handleBulkAction('archive')}
          >
            Archive
          </button>
          <button
            className="btn btn-danger"
            onClick={() => handleBulkAction('delete')}
          >
            Delete
          </button>
          <button
            className="btn btn-text"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="submissions-error">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadSubmissions}>
            Try Again
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="submissions-loading">
          <div className="spinner" />
          <p>Loading submissions...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && submissions.length === 0 && (
        <div className="submissions-empty">
          <div className="empty-icon">📭</div>
          <h2>No submissions yet</h2>
          <p>
            {statusFilter || dateFrom || dateTo
              ? 'No submissions match your filters. Try adjusting your search criteria.'
              : 'When visitors submit your form, their submissions will appear here.'}
          </p>
          {(statusFilter || dateFrom || dateTo) && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setStatusFilter('');
                setDateFrom('');
                setDateTo('');
                setPage(1);
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Submissions Table */}
      {!loading && !error && submissions.length > 0 && (
        <>
          <div className="submissions-table-container">
            <table className="submissions-table">
              <thead>
                <tr>
                  <th className="col-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === submissions.length && submissions.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="col-status">Status</th>
                  <th className="col-progress">Progress</th>
                  <th className="col-started">Started</th>
                  <th className="col-completed">Completed</th>
                  <th className="col-device">Device</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((submission) => (
                  <tr
                    key={submission.id}
                    className={selectedIds.has(submission.id) ? 'selected' : ''}
                    onClick={() => openSubmissionDetail(submission)}
                  >
                    <td className="col-checkbox" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(submission.id)}
                        onChange={() => toggleSelection(submission.id)}
                      />
                    </td>
                    <td className="col-status">
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor: STATUS_COLORS[submission.status].bg,
                          color: STATUS_COLORS[submission.status].text,
                        }}
                      >
                        {submission.status}
                      </span>
                    </td>
                    <td className="col-progress">
                      <div className="progress-bar-container">
                        <div
                          className="progress-bar"
                          style={{ width: `${submission.progress_percentage}%` }}
                        />
                        <span className="progress-text">
                          {submission.current_step}/{submission.total_steps}
                        </span>
                      </div>
                    </td>
                    <td className="col-started">
                      <span title={formatDate(submission.started_at)}>
                        {formatRelativeTime(submission.started_at)}
                      </span>
                    </td>
                    <td className="col-completed">
                      {submission.completed_at ? (
                        <span title={formatDate(submission.completed_at)}>
                          {formatRelativeTime(submission.completed_at)}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="col-device">
                      <span className="device-icon">
                        {submission.device_category === 'mobile' && '📱'}
                        {submission.device_category === 'tablet' && '📱'}
                        {submission.device_category === 'desktop' && '💻'}
                        {!submission.device_category && '—'}
                      </span>
                    </td>
                    <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-icon"
                        title="View Details"
                        onClick={() => openSubmissionDetail(submission)}
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="submissions-pagination">
              <button
                className="btn btn-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-secondary"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedSubmission && (
        <SubmissionDetailModal
          submissionId={selectedSubmission.id}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedSubmission(null);
          }}
        />
      )}
    </div>
  );
}
